import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import i18n from 'i18n-js';

import { navigationRef } from '@helpers';

import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { PartDetailsScreenName } from '@v2/pages/part/details/navigation/config';
import { PartDetailsScreen } from '@v2/pages/part/details/common/enums/partDetails.enum';
import { LevelSubScreen, Perspective } from '@v2/pages/common/enums';
import { useHandleNavigationMachinesHeader } from '@v2/pages/machines/hooks/useHandleNavigationMachineHeader';
import { usePanelViewMode } from '@v2/pages/machines/hooks/usePanelViewMode';
import { NowSubScreen } from '@v2/pages/machines/common/enums';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';
import { useDocumentsNavigation } from '@v2/pages/common/hooks/useDocumentsNavigation';
import { useHandleNavigationContextHeader } from '@v2/pages/common/hooks/useHandleNavigationContextHeader';
import { selectSelectedItem, setSelectedNCFile } from '@reducers/machines';
import { selectIsEditModeForPartSwitchOn, selectEditingPartData, setEditingPartData } from '@reducers/details';
import { generateImageURL } from '@reducers/blobStorage';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { getPartFieldValue } from '@v2/pages/machines/modules/now/components/Part/helpers';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import type { Machine } from '@interfaces/machines';
import type { PartGeneralContentProps } from '@v2/pages/machines/modules/now/components/Part/components/PartGeneralContent';

export interface UseManagePartGeneralContentParams
  extends Pick<PartGeneralContentProps, 'noMachine' | 'partDetails' | 'currentPartId' | 'partDataLoadingForList' | 'displaySinglePart'> {}

export const useManagePartGeneralContent = (params: UseManagePartGeneralContentParams) => {
  const {
    noMachine,
    partDetails: partDetailsFromProps,
    currentPartId,
    partDataLoadingForList,
    displaySinglePart,
  } = params;

  const dispatch = useAppDispatch();
  const selectedItem = useSelector(selectSelectedItem) as Machine;
  const isEditModeForPartSwitchOn = useSelector(selectIsEditModeForPartSwitchOn);
  const editingPartData = useSelector(selectEditingPartData);

  const { panelViewMode } = usePanelViewMode();
  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();
  const { handleNavigateDetailsLevel, screenConfig } = useHandleNavigationContextHeader();
  const { isMachinesScreen } = useHandleNavigationMachinesHeader();
  const { safeIsNowSubScreenCheck, safeIsDetailsScreen } = useNavigationCheck();
  const { handleNowScreenNavigation } = useDocumentsNavigation();

  const route = navigationRef?.current?.getCurrentRoute();
  const isPartPerspective = route?.params?.perspective === Perspective.PART;

  const [containerWidth, setContainerWidth] = useState<number>(0);

  const { partDetails: defaultPartDetails = {} } = selectedItem?.machineStatus ?? {};

  const partDetails = noMachine ? partDetailsFromProps : [defaultPartDetails];

  const partId = getPartFieldValue(partDetails, 'partId');
  const partName = getPartFieldValue(partDetails, 'partName');

  // Clear editing data when part changes or when not in edit mode
  useEffect(() => {
    if (!isEditModeForPartSwitchOn && editingPartData) {
      dispatch(setEditingPartData(null, null));
    } else if (editingPartData && editingPartData.partId && editingPartData.partId !== partId) {
      dispatch(setEditingPartData(null, null));
    }
  }, [partId, isEditModeForPartSwitchOn]);

  const onPartNamePress = () => {
    navigateToMainDetailsScreen({
      mainScreen: PartDetailsScreenName,
      subScreen: PartDetailsScreen.PART,
      params: {
        perspective: Perspective.PART,
        partId,
        partName,
      },
    });
  };

  const onPartImagePress = (selectedImageKey: string) => {
    if (safeIsNowSubScreenCheck) {
      handleNowScreenNavigation({ nowSubScreen: NowSubScreen.PART, params: { selectedImageKey } });
      return;
    }
    navigateToMainDetailsScreen({
      mainScreen: PartDetailsScreenName,
      subScreen: PartDetailsScreen.PART,
      levelSubScreen: LevelSubScreen.DOCUMENTS,
      params: {
        perspective: Perspective.PART,
        partId,
        partName,
        selectedImageKey,
      },
    });
  };

  const shouldShowPartNameAsText = (safeIsDetailsScreen && isPartPerspective) || isEditModeForPartSwitchOn;

  const shouldDisplaySinglePartResolved = displaySinglePart || (partDetails && partDetails.length === 1);

  const shouldDisplayMultiplePart = partDetails && (partDetails.length > 1 || partDetails.length === 0);

  const shouldNotShowLoader = useMemo(() => {
    if (isMachinesScreen) return true;
    if (shouldDisplayMultiplePart) {
      return !partDataLoadingForList;
    }
    return partId === Number(currentPartId) && !partDataLoadingForList;
  }, [isMachinesScreen, shouldDisplaySinglePartResolved, shouldDisplayMultiplePart, partDataLoadingForList, currentPartId, partDetails]);

  const ncFilesByPartIdAmount = getPartFieldValue(partDetails, 'linkedNcFiles')?.length ?? 0;
  const ncFilesLine = [
    {
      testID: TEST_IDS.partGeneralContent.linkedNcFilesLine,
      value: String(ncFilesByPartIdAmount),
      name: i18n.t('Linked NC Files'),
      onClick:
        ncFilesByPartIdAmount === 0
          ? () => {}
          : () => {
              handleNavigateDetailsLevel({ levelScreen: screenConfig?.levelScreens?.NC_FILE });
            },
    },
  ];

  const onContainerLayout = (event: { nativeEvent: { layout: { width: number } } }) => {
    const { width } = event.nativeEvent.layout;
    setContainerWidth(width);
  };

  const handleSetEditingPartData = (...args: Parameters<typeof setEditingPartData>) => {
    dispatch(setEditingPartData(...args));
  };

  const handleGenerateImageURL = (...args: Parameters<typeof generateImageURL>) => {
    dispatch(generateImageURL(...args));
  };

  const handleSetSelectedNCFile = (...args: Parameters<typeof setSelectedNCFile>) => {
    dispatch(setSelectedNCFile(...args));
  };

  return {
    panelViewMode,
    isEditModeForPartSwitchOn,
    editingPartData,
    selectedItem,
    containerWidth,
    partDetails,
    partId,
    partName,
    shouldShowPartNameAsText,
    shouldDisplaySinglePart: shouldDisplaySinglePartResolved,
    shouldDisplayMultiplePart,
    shouldNotShowLoader,
    ncFilesLine,
    onPartNamePress,
    onPartImagePress,
    onContainerLayout,
    setEditingPartData: handleSetEditingPartData,
    generateImageURL: handleGenerateImageURL,
    setSelectedNCFile: handleSetSelectedNCFile,
  };
};
